import { Feedback } from "../../../..";
import { Tag } from "../../../../../Tags";
import { feedbackService } from "../../../../Services";
import { GetColumnDefinitionsArgs } from "../Types/FeedbackDatagridInputs";
import { FeedbackRowData } from "../Types/FeedbackRowData";
import { FeedbackListCellRenderService } from "./FeedbackListCellRenderService";
import { TitleRelationshipFilterSelect } from "../Filter/TitleRelationshipFilterSelect";
import { TitleRelationshipFilter } from "../Filter/TitleRelationshipFilter";
import { sortPriority } from "../Sort/sorter";
import { AssignedUsersFilter } from "../Filter/AssignedUsersFilter";

interface IFeedbackListDataService {
  getColumnDefinitions: (args: GetColumnDefinitionsArgs) => any[];
  mapChildrenToTableData: (
    feedbacks: Feedback[],
    tags: Tag[],
    areTransitionsLoaded: boolean,
    parentId: number,
    onClickEditDueDate: (feedbackCode: string, currentDueDate: Date | undefined) => void
  ) => FeedbackRowData[];
  mapFeedbacksToTreeData: (
    feedbacks: Feedback[],
    tags: Tag[],
    areTransitionsLoaded: boolean,
    onClickEditDueDate: (feedbackCode: string, currentDueDate: Date | undefined) => void
  ) => FeedbackRowData[];
  mapFeedbacksToFlatData: (
    feedbacks: Feedback[],
    tags: Tag[],
    areTransitionsLoaded: boolean,
    onClickEditDueDate: (feedbackCode: string, currentDueDate: Date | undefined) => void
  ) => FeedbackRowData[];
}

class FeedbackListDataService implements IFeedbackListDataService {
  getColumnDefinitions(args: GetColumnDefinitionsArgs): any[] {
    const cellRenderService = new FeedbackListCellRenderService(args);

    const { enableSearch, isCMUser, styles } = args;

    return [
      {
        header: "Title",
        accessor: "title",
        type: "text",
        searchable: true && enableSearch,
        sortable: true && enableSearch,
        filterable: true && enableSearch,
        minWidth: 350,
        render: cellRenderService.renderTitle,
        valueSelector: (value: any) => value.title,
        exportValueGetter: (value: any) => value.title,
        filterRender: TitleRelationshipFilterSelect,
        filter: TitleRelationshipFilter
      },
      {
        header: "Upvotes",
        accessor: "upvotes",
        type: "number",
        searchable: false && enableSearch,
        sortable: true && enableSearch,
        filterable: true && enableSearch,
        maxWidth: 100,
        render: cellRenderService.renderUpvotes,
        valueSelector: (value: any) => value.voteCount,
        exportValueGetter: (value: any) => value.voteCount
      },
      {
        header: "Created",
        accessor: "created",
        type: "date",
        searchable: false && enableSearch,
        sortable: true && enableSearch,
        filterable: enableSearch,
        maxWidth: 100,
        render: cellRenderService.renderDate
      },
      {
        header: "Status",
        accessor: "status",
        type: "tag",
        sortable: true && enableSearch,
        searchable: false && enableSearch,
        filterable: true && enableSearch,
        maxWidth: 100,
        render: cellRenderService.renderStatus
      },
      {
        header: "Due Date",
        accessor: "dueDate",
        type: "date",
        sortable: true && enableSearch,
        searchable: false && enableSearch,
        filterable: true && enableSearch,
        maxWidth: 130,
        render: cellRenderService.renderDueDate,
        valueSelector: (value: any) => new Date(value.dueDate)
      },
      {
        header: "Priority",
        accessor: "priority",
        sortable: true && enableSearch,
        searchable: false && enableSearch,
        filterable: enableSearch,
        type: "number",
        maxWidth: 90,
        render: cellRenderService.renderPriority,
        valueSelector: (value: any) => value.priority,
        exportValueGetter: (value: any) => value.priority,
        sortBy: sortPriority
      },
      {
        header: "Author",
        accessor: "author",
        type: "tag",
        sortable: true && enableSearch,
        searchable: true && enableSearch,
        filterable: enableSearch,
        render: cellRenderService.renderAuthor
      },
      {
        header: "Tags",
        accessor: "tags",
        type: "tag",
        sortable: false && enableSearch,
        searchable: false && enableSearch,
        filterable: true && enableSearch,
        render: cellRenderService.renderTags
      },
      {
        header: "Assigned To",
        accessor: "assignedTo",
        type: "tag",
        sortable: false && enableSearch,
        searchable: false && enableSearch,
        filterable: true && enableSearch,
        hidden: !isCMUser,
        render: cellRenderService.renderAssignedTo,
        valueSelector: (value: any) => value.assignments,
        exportValueGetter: (value: any) => value.assignments.map((a: any) => a.name).join(","),
        filter: AssignedUsersFilter
      },
      {
        header: "",
        accessor: "buttons",
        sortable: false,
        searchable: false,
        render: cellRenderService.renderButtons,
        maxWidth: 100,
        visibleInExport: false
      },
      {
        header: "",
        accessor: "actions",
        sortable: false,
        searchable: false,
        render: cellRenderService.renderActions,
        maxWidth: 10,
        visibleInExport: false
      }
    ];
  }

  // Get children of specific feedback
  getChildrenFromParentId(feedbacks: Feedback[], parentId: number): Feedback[] {
    return feedbacks.filter(feedback => {
      return feedback.parentId === parentId;
    });
  }

  // All parent ids
  getParentIds(feedbacks: Feedback[]) {
    return feedbacks
      .filter(feedback => feedback.parentId !== null)
      .map(feedback => {
        return feedback.parentId;
      });
  }

  // Add children ids
  getChildrenIds(feedbacks: Feedback[]) {
    return feedbacks.filter(feedback => feedback.parentId !== null).map(feedback => feedback.id);
  }

  // All ids
  getAllIds(feedbacks: Feedback[]) {
    return feedbacks.map(feedback => feedback.id);
  }

  getChildrenFeedbacks(feedbacks: Feedback[], allIds: number[]) {
    return feedbacks.filter(feedback => feedback.parentId && allIds.includes(feedback.parentId));
  }

  // All children whose parents have been deleted
  getOrphanedChildrenFeedbacks(feedbacks: Feedback[], allIds: number[]) {
    return feedbacks.filter(feedback => feedback.parentId && !allIds.includes(feedback.parentId));
  }

  mapChildrenToTableData(
    feedbacks: Feedback[],
    tags: Tag[],
    areTransitionsLoaded: boolean,
    parentId: number,
    onClickEditDueDate: (feedbackCode: string, currentDueDate: Date | undefined) => void
  ): FeedbackRowData[] {
    const children = this.getChildrenFromParentId(feedbacks, parentId);
    const getParentIds = this.getParentIds(feedbacks);

    return children.map((f, i) => {
      if (f.assignedTo.length === 0) {
        f.assignedTo.push({
          id: -1,
          username: "unassigned",
          name: "Unassigned"
        });
      }

      return {
        index: i,
        upvotes: {
          feedbackCode: f.code,
          existingVote: f.loggedInUserVote,
          voteCount: f.voteCount
        },
        title: {
          isPrivate: f.isPrivate,
          url: feedbackService.getViewUrl(f),
          title: f.title,
          parentId: f.parentId,
          isChild: true,
          isParent: getParentIds.includes(f.id),
          isGrouped: true
        },
        created: new Date(f.dateCreated),
        status: {
          state: f.workflowState,
          name: f.workflowState.name,
          color: f.workflowState.backgroundColor
        },
        dueDate: {
          dueDate: f.dueDate ? new Date(f.dueDate) : null,
          feedbackCode: f.code,
          canEdit: f.permissions ? f.permissions.canPrioritize : false,
          onClickEdit: () => onClickEditDueDate(f.code, f.dueDate ? new Date(f.dueDate) : undefined)
        },
        priority: {
          feedbackCode: f.code,
          priority: f.priority,
          canPrioritize: f.permissions ? f.permissions.canPrioritize : false
        },
        author: f.authorData,
        tags: f.tagIds.map(t => {
          const thisTag = tags.find(t2 => t2.id === t);

          if (!thisTag) {
            return {
              id: -1,
              name: "NotFoundTag",
              color: "white"
            };
          }

          return thisTag;
        }),
        assignedTo: {
          feedback: f,
          assignments: f.assignedTo.map(a => {
            return {
              name: a.name
            };
          })
        },
        buttons: {
          feedback: f,
          areTransitionsLoaded
        },
        actions: {
          feedback: f
        },
        subRows: this.mapChildrenToTableData(
          feedbacks,
          tags,
          areTransitionsLoaded,
          f.id,
          onClickEditDueDate
        )
      };
    });
  }

  mapFeedbacksToTreeData(
    feedbacks: Feedback[],
    tags: Tag[],
    areTransitionsLoaded: boolean,
    onClickEditDueDate: (feedbackCode: string, currentDueDate: Date | undefined) => void
  ): FeedbackRowData[] {
    const parentIds = this.getParentIds(feedbacks);
    const allIds = this.getAllIds(feedbacks);

    const noChildrenFeedbacks: Feedback[] = feedbacks.filter(feedback => {
      return feedback.parentId == null;
    });
    const childrenFeedbacks: Feedback[] = this.getChildrenFeedbacks(feedbacks, allIds);
    const orphanedChildrenFeedbacks: Feedback[] = this.getOrphanedChildrenFeedbacks(
      feedbacks,
      allIds
    );

    const mainFeedbacks = noChildrenFeedbacks.concat(orphanedChildrenFeedbacks);

    return mainFeedbacks.map((f, i) => {
      if (f.assignedTo.length === 0) {
        f.assignedTo.push({
          id: -1,
          username: "unassigned",
          name: "Unassigned"
        });
      }

      return {
        index: i,
        upvotes: {
          feedbackCode: f.code,
          existingVote: f.loggedInUserVote,
          voteCount: f.voteCount
        },
        title: {
          isPrivate: f.isPrivate,
          url: feedbackService.getViewUrl(f),
          title: f.title,
          parentId: f.parentId,
          isChild: false,
          isParent: parentIds.includes(f.id),
          isGrouped: parentIds.includes(f.id)
        },
        created: new Date(f.dateCreated),
        status: {
          state: f.workflowState,
          name: f.workflowState.name,
          color: f.workflowState.backgroundColor
        },
        dueDate: {
          dueDate: f.dueDate ? new Date(f.dueDate) : null,
          feedbackCode: f.code,
          canEdit: f.permissions ? f.permissions.canPrioritize : false,
          onClickEdit: () => onClickEditDueDate(f.code, f.dueDate ? new Date(f.dueDate) : undefined)
        },
        priority: {
          feedbackCode: f.code,
          priority: f.priority,
          canPrioritize: f.permissions ? f.permissions.canPrioritize : false
        },
        author: f.authorData,
        tags: f.tagIds.map(t => {
          const thisTag = tags.find(t2 => t2.id === t);

          if (!thisTag) {
            return {
              id: -1,
              name: "NotFoundTag",
              color: "white"
            };
          }

          return thisTag;
        }),
        assignedTo: {
          feedback: f,
          assignments: f.assignedTo.map(a => {
            return {
              name: a.name
            };
          })
        },
        buttons: {
          feedback: f,
          areTransitionsLoaded
        },
        actions: {
          feedback: f
        },
        subRows: this.mapChildrenToTableData(
          childrenFeedbacks,
          tags,
          areTransitionsLoaded,
          f.id,
          onClickEditDueDate
        )
      };
    });
  }

  mapFeedbacksToFlatData(
    feedbacks: Feedback[],
    tags: Tag[],
    areTransitionsLoaded: boolean,
    onClickEditDueDate: (feedbackCode: string, currentDueDate: Date | undefined) => void
  ): FeedbackRowData[] {
    const parentIds = this.getParentIds(feedbacks);
    const childrenIds = this.getChildrenIds(feedbacks);

    return feedbacks.map((f, i) => {
      if (f.assignedTo.length === 0) {
        f.assignedTo.push({
          id: -1,
          username: "unassigned",
          name: "Unassigned"
        });
      }

      return {
        index: i,
        upvotes: {
          feedbackCode: f.code,
          existingVote: f.loggedInUserVote,
          voteCount: f.voteCount
        },
        title: {
          isPrivate: f.isPrivate,
          url: feedbackService.getViewUrl(f),
          title: f.title,
          parentId: f.parentId,
          isChild: childrenIds.includes(f.id),
          isParent: parentIds.includes(f.id),
          isGrouped: childrenIds.includes(f.id) || parentIds.includes(f.id)
        },
        created: new Date(f.dateCreated),
        status: {
          state: f.workflowState,
          name: f.workflowState.name,
          color: f.workflowState.backgroundColor
        },
        dueDate: {
          dueDate: f.dueDate ? new Date(f.dueDate) : null,
          feedbackCode: f.code,
          canEdit: f.permissions ? f.permissions.canPrioritize : false,
          onClickEdit: () => onClickEditDueDate(f.code, f.dueDate ? new Date(f.dueDate) : undefined)
        },
        priority: {
          feedbackCode: f.code,
          priority: f.priority,
          canPrioritize: f.permissions ? f.permissions.canPrioritize : false
        },
        author: f.authorData,
        tags: f.tagIds.map(t => {
          const thisTag = tags.find(t2 => t2.id === t);

          if (!thisTag) {
            return {
              id: -1,
              name: "NotFoundTag",
              color: "white"
            };
          }

          return thisTag;
        }),
        assignedTo: {
          feedback: f,
          assignments: f.assignedTo.map(a => {
            return {
              name: a.name
            };
          })
        },
        buttons: {
          feedback: f,
          areTransitionsLoaded
        },
        actions: {
          feedback: f
        }
      };
    });
  }
}

export const feedbackListDataService = new FeedbackListDataService();
